import React, { useEffect, useState } from "react";

const FALLBACK_RATES = { ZAR: 18, EUR: 0.9, ZMW: 24 };

export default function QuotePage({ systemType, costs, kWp, currency }) {
  const [fxRates, setFxRates] = useState(FALLBACK_RATES);
  const [pegPricing, setPegPricing] = useState({ 500: 65, 1000: 48, 2000: 42 });

  useEffect(() => {
    if (typeof fetch === "undefined") return;
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.rates) {
          setFxRates({
            ZAR: data.rates.ZAR,
            EUR: data.rates.EUR,
            ZMW: data.rates.ZMW,
          });
        }
      })
      .catch(() => {});
  }, []);

  const sumPerKwp = (items) =>
    items.reduce((s, i) => s + (i.children ? sumPerKwp(i.children) : i.perKwp), 0);

  const flatten = (items, depth = 0, path = []) =>
    items.flatMap((it) => {
      const currentPath = [...path, it.id];
      const perKwp = it.children ? sumPerKwp(it.children) : it.perKwp;
      const row = { ...it, perKwp, depth, path: currentPath, leaf: !it.children };
      return [row, ...(it.children ? flatten(it.children, depth + 1, currentPath) : [])];
    });

  const rows = flatten(costs[systemType]).filter((r) => r.leaf && r.includeInQuote);

  const formatMoney = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatWhole = (n) => Math.round(n).toLocaleString();

  const rate = fxRates[currency];
  const totalPerKwp = rows.reduce((s, r) => s + r.perKwp, 0);
  const totalCapex = totalPerKwp * kWp;

  const updatePegPrice = (size, value) =>
    setPegPricing((p) => ({ ...p, [size]: +value }));

  return (
    <div className="quote-page">
      <h2>Quotation</h2>
      <table className="cost-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>$/kWp</th>
            <th>Total $</th>
            <th>Total {currency}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const total = item.perKwp * kWp;
            const converted = rate ? total * rate : null;
            return (
              <tr key={item.path.join("/")}>
                <td style={{ paddingLeft: item.depth * 16 }}>{item.label}</td>
                <td>{formatMoney(item.perKwp)}</td>
                <td>{formatWhole(total)}</td>
                <td>{converted !== null ? formatWhole(converted) : "..."}</td>
              </tr>
            );
          })}
          <tr>
            <td><b>Total</b></td>
            <td><b>{formatMoney(totalPerKwp)}</b></td>
            <td><b>{formatWhole(totalCapex)}</b></td>
            <td><b>{rate ? formatWhole(totalCapex * rate) : "..."}</b></td>
          </tr>
        </tbody>
      </table>

      <h3>Additional items</h3>
      <ul>
        <li>Cable management sundries</li>
        <li>Pull out test service (Excluding travel cost)</li>
        <li>
          Tools recommended for PEG installation
          <ul>
            <li>Hilti TE60-A36</li>
            <li>Hilti NPR 032 PE-A22</li>
            <li>
              PEG pressing jaw ND14 PB-2 or ND16 PB-2 or ND20 PB-2 depending on rod diameter
            </li>
          </ul>
        </li>
        <li>1 Day training on PEG installation</li>
        <li>Logistics to site</li>
      </ul>

      <h3>PEG System Pricing (€/kWp FOB Mumbai)</h3>
      <table className="cost-table">
        <thead>
          <tr>
            <th>System size (kWp)</th>
            <th>€/kWp</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(pegPricing).map(([size, price]) => (
            <tr key={size}>
              <td>{size}</td>
              <td>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => updatePegPrice(size, e.target.value)}
                />
                {size === "500" && <span style={{ marginLeft: 4 }}>(MOQ surcharge)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

